import React, { useCallback, useState } from 'react';
import { Skeleton } from '@monksflow/monks-ui';
import clsx from 'clsx';

import type { OptimizedImageProps } from './OptimizedImage.types';
import { useTranslation } from 'react-i18next';

/**
 * Lazy, progressive `<img>` wrapper. Refactored to remove `useEffect`:
 *
 * - IntersectionObserver is attached via a **callback ref** on the container. The ref fires when
 *   the container node mounts/unmounts, which is exactly when we need to observe/disconnect. No
 *   effect + `useRef` dance, no stale-closure risk.
 * - Load / error state lives in a small inner component keyed by the resolved `src`. When the
 *   caller swaps `src`, React remounts the inner image, so `isLoaded`/`isError` reset for free
 *   (no effect watching `[src]` to reset flags).
 * - Progressive `webpSrc → src` fallback is done in an `onError` handler on the rendered `<img>`
 *   itself. The old preload `new Image()` roundtrip was redundant (the browser already fetches the
 *   rendered `src`), and it forced an effect just to move a URL between two state slots.
 *
 * Most of this surface is unused. Across every call site only `src`, `alt`, `width`, `height`,
 * `className` and `priority` are ever passed, and `showSkeleton` exactly once — so nearly every
 * caller renders no placeholder at all. `../ThumbnailImage` already covers that live subset and
 * owns load state, the placeholder and the error fallback, which makes it the natural leaf for
 * this to compose. The two disagree on one point a merge would have to settle: this resets to the
 * placeholder whenever `src` changes, which is wrong for presigned URLs, where the same asset is
 * re-signed on every fetch.
 */
export const OptimizedImage = ({
  src,
  alt,
  width,
  height,
  className,
  containerClassName,
  containerStyle,
  style,
  lazy = true,
  showSkeleton = false,
  placeholder,
  errorFallback,
  webpSrc,
  sources = [],
  lowQualitySrc,
  onLoad,
  onError,
  rootMargin = '50px',
  threshold = 0.1,
  priority = false,
}: OptimizedImageProps) => {
  const { t } = useTranslation('common');

  const [isInView, setIsInView] = useState(!lazy || priority);

  // Callback ref on the container: attach IntersectionObserver when the node mounts, disconnect
  // when the node detaches or when lazy tracking is no longer needed. `useCallback` keeps identity
  // stable so the ref fires only on real attach/detach, not every render.
  const observerRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return;
      if (!lazy || priority || isInView) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsInView(true);
              observer.disconnect();
            }
          });
        },
        { rootMargin, threshold },
      );
      observer.observe(node);
      return () => observer.disconnect();
    },
    // We intentionally do NOT depend on `isInView` becoming true: once the observer flips it, the
    // node's rerender goes through the ref cleanup path (React 19 callback-ref cleanup) which
    // disconnects. Adding `isInView` here would just re-run attach with the guard returning early.
    [lazy, priority, rootMargin, threshold, isInView],
  );

  // reused at 2 call sites (loaded-image placeholder slot + the
  // not-in-view branch below) — computed once as a plain value rather than a
  // `renderPlaceholder`-style closure, so both sites reference the same node.
  let placeholderNode: React.ReactNode = null;
  if (placeholder) {
    placeholderNode = placeholder;
  } else if (showSkeleton) {
    placeholderNode = (
      <Skeleton
        className={className}
        style={{ width: width || '100%', height: height || '100%', ...style }}
      />
    );
  }

  const errorNode = errorFallback ?? (
    <div
      className={clsx('flex items-center justify-center bg-muted text-muted-foreground', className)}
      style={{ width, height, ...style }}
    >
      <span className="text-sm">{t('components.optimizedImage.failedToLoadImage')}</span>
    </div>
  );

  return (
    <div ref={observerRef} className={clsx('relative', containerClassName)} style={containerStyle}>
      {isInView ? (
        // Keyed on the resolved primary URL so `isLoaded`/`isError` reset on src changes via remount
        // (spec Rule 5: reset with key, not dependency choreography).
        <ImageInner
          key={webpSrc || src}
          src={src}
          webpSrc={webpSrc}
          alt={alt}
          className={className}
          style={style}
          width={width}
          height={height}
          lowQualitySrc={lowQualitySrc}
          sources={sources}
          priority={priority}
          onLoad={onLoad}
          onError={onError}
          errorNode={errorNode}
          placeholderNode={placeholderNode}
        />
      ) : (
        placeholderNode
      )}
    </div>
  );
};

type ImageInnerProps = Pick<
  OptimizedImageProps,
  | 'src'
  | 'webpSrc'
  | 'alt'
  | 'className'
  | 'style'
  | 'width'
  | 'height'
  | 'lowQualitySrc'
  | 'sources'
  | 'priority'
  | 'onLoad'
  | 'onError'
> & {
  errorNode: React.ReactNode;
  placeholderNode: React.ReactNode;
};

function ImageInner({
  src,
  webpSrc,
  alt,
  className,
  style,
  width,
  height,
  lowQualitySrc,
  sources = [],
  priority,
  onLoad,
  onError,
  errorNode,
  placeholderNode,
}: ImageInnerProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isError, setIsError] = useState(false);
  // `currentSrc` starts at the preferred URL (webp when available, else src). On error we fall back
  // to `src`. All state transitions happen inside handlers — no effect chains.
  const [currentSrc, setCurrentSrc] = useState<string>(webpSrc || src);
  const [triedFallback, setTriedFallback] = useState(false);

  if (isError) return <>{errorNode}</>;

  const imageStyle: React.CSSProperties = {
    width,
    height,
    opacity: isLoaded ? 1 : lowQualitySrc ? 0.7 : 0,
    ...style,
  };

  const handleLoad = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  const handleError = () => {
    if (webpSrc && !triedFallback && currentSrc === webpSrc) {
      // Swap to the non-webp source and let the browser retry.
      setTriedFallback(true);
      setCurrentSrc(src);
      return;
    }
    setIsError(true);
    onError?.();
  };

  const imgEl = (
    <img
      src={currentSrc}
      alt={alt}
      className={clsx('transition-opacity duration-slow ease-in-out', className)}
      style={imageStyle}
      loading={priority ? 'eager' : 'lazy'}
      onLoad={handleLoad}
      onError={handleError}
    />
  );

  return (
    <>
      {!isLoaded && placeholderNode}
      {sources.length > 0 ? (
        <picture>
          {sources.map((source, index) => (
            <source key={index} srcSet={source.srcSet} media={source.media} type={source.type} />
          ))}
          {imgEl}
        </picture>
      ) : (
        imgEl
      )}
    </>
  );
}

export default OptimizedImage;
